import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Camera, Calculator, Megaphone, ChevronRight, Users, Clock, TrendingUp, CheckCircle2 } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Workflow Step Component with Visual
const WorkflowStep = ({ number, title, description, details, icon: Icon, color, image }) => (
  <div className="relative">
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      {/* Visual Side */}
      <div className={`relative ${number % 2 === 0 ? 'lg:order-2' : ''}`}>
        <div 
          className="rounded-2xl overflow-hidden"
          style={{ boxShadow: `0 20px 50px ${color}40` }}
        >
          <img 
            src={image} 
            alt={title}
            className="w-full h-72 object-cover"
          />
        </div>
        {/* Step Number Circle */}
        <div 
          className="absolute -top-5 -left-5 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold z-10"
          style={{ 
            background: color,
            color: "white",
            boxShadow: `0 10px 30px ${color}60`,
            border: "4px solid var(--background-100)"
          }}
        >
          {number}
        </div>
      </div>
      
      {/* Content Side */}
      <div className={number % 2 === 0 ? 'lg:order-1' : ''}>
        <div className="flex items-center gap-4 mb-4">
          <div 
            className="p-3 rounded-xl"
            style={{ background: `${color}20` }}
          >
            <Icon size={28} style={{ color: color }} />
          </div>
          <h3 
            className="text-3xl font-bold"
            style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
          >
            {title}
          </h3>
        </div>
        
        <p 
          className="text-lg mb-4"
          style={{ color: "var(--text-100)", lineHeight: "1.7" }}
        >
          {description}
        </p>
        
        <div 
          className="p-5 rounded-xl"
          style={{ background: "var(--background-300)" }}
        >
          <p style={{ color: "var(--text-200)", lineHeight: "1.6" }}>
            {details}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Benefit Card Component
const BenefitCard = ({ icon: Icon, title, description, stat }) => (
  <div 
    className="card p-8 text-center relative overflow-hidden group"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    {/* Background Pattern */}
    <div 
      className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity"
      style={{
        backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
        backgroundSize: "20px 20px"
      }}
    />
    
    <div 
      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 relative z-10"
      style={{ background: "var(--primary-100)" }}
    >
      <Icon size={32} style={{ color: "white" }} />
    </div>
    
    {stat && (
      <div 
        className="text-4xl font-bold mb-2"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
      >
        {stat}
      </div>
    )}
    
    <h4 
      className="text-xl font-bold mb-3 relative z-10"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h4>
    <p style={{ color: "var(--text-200)", fontSize: "0.95rem", position: "relative", zIndex: 10 }}>
      {description}
    </p>
  </div>
);

// Main Sales Workflow Page Component
const SalesWorkflowPage = () => {
  const steps = [
    {
      number: 1,
      title: "Appraise",
      icon: Camera,
      color: "#0038a8",
      image: "https://www.machinerymarketed.com/app-appraisal.jpg",
      description: "The salesperson completes the trade appraisal using MachineryMarketed.",
      details: "They're prompted to capture all required machine details, condition information, photos, customer expectations, and any relevant notes. Everything is gathered once, properly, at the point of inspection — no paperwork, no chasing information later."
    },
    {
      number: 2,
      title: "Value",
      icon: Calculator,
      color: "#0049db",
      image: "https://www.machinerymarketed.com/app-valuation.jpg",
      description: "With a single action, the appraisal is sent as a clear, concise PDF to the person responsible for valuing trades.",
      details: "They receive everything needed to set an accurate value. AI assists in the background by reviewing market data and comparable machines, helping support consistent and confident decisions. Once approved, the salesperson is notified and the quotation is produced using the dealership's existing quoting system."
    },
    {
      number: 3,
      title: "Market",
      icon: Megaphone,
      color: "#0f5fff",
      image: "https://www.machinerymarketed.com/app-machine-details.jpg",
      description: "When the deal is done, the same appraisal data and photos are used to prepare the machine for sale.",
      details: "After selecting images and refining the description, a professional advertisement is pushed live — with a click — to the chosen websites, platforms, and social channels. The machine reaches the market faster, sells sooner, and the team stays focused on customers and closing deals."
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section with Image */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/tractor_loader_pair.jpg" 
              alt="Equipment"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-16">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Users size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>For Sales Teams</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Sales <span className="text-gradient">Workflow</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed is built around a simple, three-step sales workflow that helps 
                dealerships <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>move trades faster</span> and{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>remove friction</span> from the process.
              </p>
            </div>
          </div>
        </section>

        {/* Workflow Steps Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="space-y-24 max-w-6xl mx-auto">
              {steps.map((step, index) => (
                <WorkflowStep key={index} {...step} />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 relative" style={{ background: "var(--background-200)" }}>
          {/* Decorative Elements */}
          <div 
            className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-5"
            style={{ background: "var(--primary-300)", filter: "blur(60px)" }}
          />
          <div 
            className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-5"
            style={{ background: "var(--primary-300)", filter: "blur(80px)" }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Benefits for Your Sales Team
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                What this means for your day-to-day operations
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <BenefitCard 
                icon={Clock}
                title="Save Time"
                stat="10-15 min"
                description="Average appraisal time. No more chasing photos or information after the fact."
              />
              <BenefitCard 
                icon={TrendingUp}
                title="Close Faster"
                stat="2x"
                description="Faster valuations mean quicker quotes. Quicker quotes mean more deals won."
              />
              <BenefitCard 
                icon={Users}
                title="Stay Focused"
                description="Let the system handle the marketing. Your team focuses on customers and closing deals."
              />
            </div>
          </div>
        </section>

        {/* Summary Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden"
              style={{ maxWidth: "1000px", margin: "0 auto" }}
            >
              {/* Background */}
              <div className="absolute inset-0 z-0">
                <img 
                  src="https://www.machinerymarketed.com/hero_excavator.jpg" 
                  alt="Equipment"
                  className="w-full h-full object-cover"
                  style={{ opacity: 0.2 }}
                />
                <div 
                  className="absolute inset-0"
                  style={{ background: "var(--background-200)" }}
                />
              </div>
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <div className="flex justify-center gap-8 mb-8 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={24} style={{ color: "var(--primary-300)" }} />
                    <span style={{ color: "var(--text-100)" }}>Less Admin</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={24} style={{ color: "var(--primary-300)" }} />
                    <span style={{ color: "var(--text-100)" }}>More Deals</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={24} style={{ color: "var(--primary-300)" }} />
                    <span style={{ color: "var(--text-100)" }}>Faster Movement</span>
                  </div>
                </div>
                
                <h2 
                  className="text-3xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  A streamlined workflow that connects your sales team, valuation managers, 
                  and marketing efforts in one seamless flow.
                </h2>
                
                <div className="flex flex-wrap justify-center gap-4">
                  <Link to="/how-it-works" className="btn-outline">
                    See Full Process
                  </Link>
                  <Link to="/faq" className="btn-outline">
                    Read FAQ
                  </Link>
                  <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                    Request a Demo <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SalesWorkflowPage;
